'use client'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { safeTranslateColor } from '@/lib/helpers/sanitize-i18n-keys'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ImageWithFallback as Image } from '../image-with-fallback'
import { useProductCardContext } from './context/product-card-context'

export const ProductItemVariant = () => {
  const { product, selectedColor, onChangeColorVariant } =
    useProductCardContext()

  const t = useTranslations()

  const [expanded, setExpanded] = useState(false)
  const [maxVisible, setMaxVisible] = useState(3)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const colors = useMemo(() => {
    return Object.entries(product?.mapped_variants ?? {})
  }, [product?.mapped_variants])

  const updateVisibleVariants = useCallback(() => {
    const container = containerRef.current
    const button = buttonRef.current

    if (!container || !button || colors.length === 0) return

    const containerWidth = container.clientWidth
    const buttonWidth = button.offsetWidth || 30

    const gap = window.innerWidth >= 1024 ? 8 : 6
    const plusButtonWidth = 10

    const totalAvailableWidth = containerWidth - plusButtonWidth - gap
    const maxItems = Math.floor(totalAvailableWidth / (buttonWidth + gap))

    setMaxVisible(Math.max(1, Math.min(maxItems, colors.length)))
  }, [colors.length])

  useEffect(() => {
    updateVisibleVariants()

    const resizeObserver = new ResizeObserver(updateVisibleVariants)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [updateVisibleVariants])

  const visibleVariants = expanded ? colors : colors.slice(0, maxVisible)

  if (colors.length === 0) return null

  return (
    <div
      ref={containerRef}
      className='flex w-full flex-wrap items-center gap-1.5 lg:gap-2'
    >
      {visibleVariants.map(([color, variants], index) => (
        <button
          key={color}
          ref={index === 0 ? buttonRef : null}
          className={cn(
            'block h-[17px] w-[30px] rounded-full bg-center bg-no-repeat ring-1 ring-neutral-200 ring-offset-1 hover:ring-primary lg:h-5.5 lg:w-9 lg:min-w-9 lg:ring-offset-2',
            selectedColor === color && 'ring-primary'
          )}
          title={safeTranslateColor(t, color)}
          aria-label={`Chọn màu ${color}`}
          onClick={() => onChangeColorVariant({ color, variants })}
        >
          <Image
            src={ImageHelper.getImageUrl(
              variants[0].colorImage,
              '',
              160,
              160,
              85
            )}
            alt={color}
            width={160}
            height={160}
            className='h-full w-full rounded-full object-cover'
          />
        </button>
      ))}

      {!expanded && colors.length > maxVisible && (
        <button
          className='font-sans text-sm text-neutral-900 transition-colors hover:text-primary'
          onClick={() => setExpanded(true)}
        >
          +{colors.length - maxVisible}
        </button>
      )}
    </div>
  )
}
